<template>
  <v-responsive @mouseenter.native="onCarouselHover" @mouseleave.native="onCarouselMouseLeave">
    <v-carousel
      v-if="images.length > 1"
      ref="viewer"
      v-model="selectedImageIndex"
      :class="{
        big: inModal
      }"
      :continuous="false"
      :height="viewerImageHeight"
      :show-arrows="!hideCarouselArrows"
      :hide-delimiters="inModal"
    >
      <v-carousel-item v-for="(image, index) in images" :key="image.id" :min-width="viewerWidth" class="image-viewer-item-container">
        <image-viewer-image-item
          v-if="viewerWidth"
          :image="image"
          :inactive="index !== selectedImageIndex"
          :viewer-width="viewerWidth"
          :in-modal="inModal"
          contain
          :max-height="viewerImageHeight"
          @click.native="onImageClick"
          @modal-outside-image-click="$emit('modal-outside-image-click')"
        />
      </v-carousel-item>
    </v-carousel>
    <div v-else ref="viewer" class="image-viewer-item-container d-flex">
      <image-viewer-image-item
        v-if="viewerWidth"
        :image="images[0]"
        :viewer-width="viewerWidth"
        :in-modal="inModal"
        eager
        :contain="inModal"
        :max-height="viewerSingleImageMaxHeight"
        @click.native="onImageClick"
        @modal-outside-image-click="$emit('modal-outside-image-click')"
      />
    </div>
  </v-responsive>
</template>

<script>
import * as images from "@/services/images.js"

import ImageViewerImageItem from "@/components/files/ImageViewerImageItem.vue"

import { useLayout } from "vuetify"

const changingImageMeasurementsDelayedTimeout = 500

export default {
  components: {
    ImageViewerImageItem
  },
  props: {
    images: {
      type: Array,
      required: true
    },
    inModal: {
      type: Boolean,
      default: false
    },
    initialImageIndex: {
      type: Number,
      default: 0
    },
    changingImageMeasurementsDelayed: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const { mainRect } = useLayout()
    return { mainRect }
  },
  data() {
    return {
      viewerImageHeight: 400,
      viewerImageWidth: 0,
      viewerWidth: 0,
      hideCarouselArrows: false,
      selectedImageIndex: 0,
      isHoveringOverCarousel: false,
      timeoutHandler: null
    }
  },
  computed: {
    marginsHorizontal() {
      return this.inModal && !this.$vuetify.display.mobile ? 192 : 0
    },
    verticalMargins() {
      if (this.inModal) {
        return this.$vuetify.display.mobile ? 96 : 192
      }

      return this.$vuetify.display.mobile ? this.mainRect.top + 48 : 0
    },
    viewerElement() {
      return this.$refs.viewer.$el || this.$refs.viewer
    },
    viewerSingleImageMaxHeight() {
      return this.inModal ? this.viewerImageHeight : Math.min(Math.floor(this.viewerWidth * 2))
    }
  },
  created() {
    this.selectImage(this.initialImageIndex)
  },
  mounted() {
    this.$eventBus.$on("window-resize", this.setViewerMeasurementsOnResize)
    this.setViewerMeasurements(this.changingImageMeasurementsDelayed)
    if (!this.inModal) {
      this.onVisible()
    }
  },
  beforeUnmount() {
    this.$eventBus.$off("window-resize", this.setViewerMeasurementsOnResize)
    clearTimeout(this.timeoutHandler)
  },
  methods: {
    setViewerMeasurementsOnResize() {
      // Sleep is needed because otherwise image width changes on resize happens before feed's width
      this.setViewerMeasurements(true)
    },
    sleep(ms) {
      clearTimeout(this.timeoutHandler)
      return new Promise((resolve) => (this.timeoutHandler = setTimeout(resolve, ms)))
    },
    async setViewerMeasurements(delayed) {
      if (delayed) {
        await this.sleep(changingImageMeasurementsDelayedTimeout)
      }
      await this.$nextTick()

      if (this.$refs.viewer) {
        this.setViewerWidth()
        this.setViewerImageHeight()
      }
    },
    setViewerWidth() {
      // V-img automatic width is not always integer which causes blank 1px space inside carousel.
      this.viewerWidth = this.inModal ? window.innerWidth - this.marginsHorizontal : this.viewerElement.clientWidth
    },
    setViewerImageHeight() {
      const maxFromImages = Math.floor(this.getMaxHeightFromImages(this.viewerWidth))

      if (this.inModal) {
        this.viewerImageHeight = Math.min(maxFromImages, window.innerHeight - this.verticalMargins)
      } else {
        const heightLimit = window.innerHeight <= this.viewerElement.clientWidth ? window.innerHeight - this.verticalMargins : this.viewerElement.clientWidth

        this.viewerImageHeight = Math.min(maxFromImages, heightLimit)
      }
    },
    getMaxHeightFromImages(maxWidth) {
      try {
        const versions = this.images.map((i) => images.getWidestUncroppedVersion(i))
        const scaledHeights = versions.map((v) => (v.height / v.width) * maxWidth)
        return scaledHeights.reduce((a, b) => Math.max(a, b))
      } catch (err) {
        console.error(err)
        return maxWidth
      }
    },
    selectImage(selectedImageIndex) {
      this.selectedImageIndex = selectedImageIndex
    },
    onCarouselHover() {
      this.isHoveringOverCarousel = true
      this.hideCarouselArrows = false
      this.$emit("mouseenter")
    },
    onCarouselMouseLeave() {
      if (!this.inModal) {
        this.isHoveringOverCarousel = false
        this.hideCarouselArrowsIn3Seconds()
      }
    },
    onVisible() {
      this.hideCarouselArrows = false
      this.hideCarouselArrowsIn3Seconds()
    },
    hideCarouselArrowsIn3Seconds() {
      setTimeout(() => {
        this.hideCarouselArrows = !this.isHoveringOverCarousel
      }, 3000)
    },
    onImageClick() {
      this.$emit("image-click", { selectedImageIndex: this.selectedImageIndex })
      this.toggleArrowsOnMobileModal()
    },
    toggleArrowsOnMobileModal() {
      if (this.$vuetify.display.mobile) {
        this.hideCarouselArrows = !this.hideCarouselArrows
      }
    }
  }
}
</script>

<style lang="scss" scoped>
:deep(.v-carousel) {
  position: relative;
  overflow: visible;
  box-shadow: none;

  &:not(.big) {
    margin-bottom: 32px;
    background-color: var(--post-detail-10);
  }

  .v-window__container {
    display: flex;
    justify-content: center;
  }

  :deep(.v-carousel__item) {
    margin: 0 auto;
  }

  .v-window__left {
    left: 8px;
  }

  .v-window__right {
    right: 8px;
  }

  .v-window__controls {
    margin: 0;

    .v-btn {
      width: 24px;
      height: 24px !important;
      min-width: 0 !important;
      z-index: var(--z-index-elevated);
      box-shadow: var(--shadow-1) !important;
      background-color: var(--post-background) !important;

      i {
        font-size: 20px !important;
        color: var(--post-tint) !important;
      }
    }
  }

  .v-carousel__controls {
    pointer-events: none;
    background-color: transparent;
    bottom: -32px;
    height: 32px;
    transition: none !important;

    .v-btn {
      width: 8px !important;
      height: 8px !important;
      margin: 0 4px !important;
      padding: 0 !important;
      color: var(--post-text-30) !important;

      &--active {
        color: var(--post-tint) !important;
      }

      &::before {
        display: none !important;
      }

      .v-icon {
        opacity: 1;
      }

      i {
        transition: none !important;
        font-size: 10px !important;
      }
    }
  }

  &.big {
    .v-window__left {
      left: 16px;
    }

    .v-window__right {
      right: 16px;
    }

    .v-window__left,
    .v-window__right {
      position: fixed;

      width: 32px;
      height: 32px !important;

      i {
        font-size: 28px !important;
      }
    }
  }

  .v-responsive__content {
    display: flex;
    justify-content: center;
  }
}

:deep(.v-responsive__content) {
  // Fix horizontal scroll bar when switching images on mobile
  overflow: hidden;
}

.v-window-x-transition-enter-active,
.v-window-x-transition-leave-active,
.v-window-x-reverse-transition-enter-active,
.v-window-x-reverse-transition-leave-active {
  transition: all 0.4s ease-out;
}
</style>
